import {
  FILTER_KEYS,
  FILTER_VALUES,
  IN_FILTER_PROVIDER_VALUES,
  IS_FILTER_PROVIDER_VALUES,
  LINKED_FILTER_PROVIDER_VALUES,
  SORT_FILTER_PROVIDER_VALUES,
  SORT_REACTIONS_FILTER_PROVIDER_VALUES,
} from '../constants/filter-constants'
import type {FilterQuery} from '../filter-query'
import {
  type FilterBlock,
  type FilterConfig,
  type FilterKey,
  FilterProviderType,
  type FilterSuggestion,
  type IN_FILTER_PROVIDER_VALUE_KEYS,
  type IndexedBlockValueItem,
  type IS_FILTER_PROVIDER_VALUE_KEYS,
  type LINKED_FILTER_PROVIDER_VALUE_KEYS,
  type SORT_FILTER_PROVIDER_VALUE_KEYS,
  type SuppliedFilterProviderOptions,
} from '../types'
import {
  caseInsensitiveStringCompare,
  getFilterValue,
  getFilterValuesByStateType,
  getUnescapedFilterValue,
} from '../utils'
import {getEmptyValueValidation, getMultiValueValidation, StaticFilterProvider} from './static'

type SelectFilterOptions = {
  strictMatching?: boolean
} & SuppliedFilterProviderOptions

export class SelectFilterProvider extends StaticFilterProvider {
  strictMatching: boolean

  constructor(filterKey: FilterKey, filterValues: FilterSuggestion[], options?: SelectFilterOptions) {
    super(filterKey, filterValues, options)
    this.filterValues = filterValues.map(fv => ({...fv, ariaLabel: `${fv.displayName}, ${this.displayName}`}))
    this.strictMatching = options?.strictMatching ?? true
    this.type = this.strictMatching ? FilterProviderType.Select : FilterProviderType.Text
  }

  override validateFilterBlockValues(
    filterQuery: FilterQuery,
    block: FilterBlock,
    values: IndexedBlockValueItem[],
    config: FilterConfig,
  ): IndexedBlockValueItem[] {
    return values.map((v, i) => {
      if (!this.options.filterTypes.multiValue && i > 0) {
        return getMultiValueValidation(v, this.key)
      }

      const extractedValue = getUnescapedFilterValue(v.value)

      if (!extractedValue) return getEmptyValueValidation(v, this.key)

      const typeValidity =
        this.filterValues.find(fv => {
          return (
            getFilterValue(fv.value) === extractedValue ||
            (config?.aliasMatching && fv.aliases?.includes(extractedValue))
          )
        }) !== undefined

      const suggestion = this.filterValues.find(fv =>
        caseInsensitiveStringCompare(getFilterValue(fv.value) ?? '', extractedValue),
      )
      if (suggestion) {
        // We explicitly set the value to the extracted value here because the suggestion value may be different
        return {...v, ...suggestion, valid: true, value: v.value}
      } else {
        return {...v, valid: this.strictMatching ? typeValidity : true}
      }
    })
  }
}

export class IsFilterProvider extends SelectFilterProvider {
  constructor(
    valueIds?: IS_FILTER_PROVIDER_VALUE_KEYS[],
    options?: SuppliedFilterProviderOptions,
    /*
     * Controls visibility of 'blocked' and 'blocking' values based on issue_dependencies feature flag
     * @default false
     */
    issueDependenciesEnabled: boolean = false,
  ) {
    let values = valueIds?.map(id => IS_FILTER_PROVIDER_VALUES[id]).filter(Boolean) ?? []

    if (!issueDependenciesEnabled) {
      values = values.filter(item => item.value !== 'blocked' && item.value !== 'blocking')
    }

    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {valueless: false}}
    super(FILTER_KEYS.is, values?.length > 0 ? values : FILTER_VALUES.is, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, hasValue: false, ...options?.filterTypes},
    })
  }
}

export class InFilterProvider extends SelectFilterProvider {
  constructor(valueIds?: IN_FILTER_PROVIDER_VALUE_KEYS[], options?: SuppliedFilterProviderOptions) {
    const values = valueIds?.map(id => IN_FILTER_PROVIDER_VALUES[id]).filter(Boolean) ?? []
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {valueless: false, multiValue: true}}
    super(FILTER_KEYS.in, values?.length > 0 ? values : FILTER_VALUES.in, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, ...options?.filterTypes},
    })
  }
}

export class LinkedFilterProvider extends SelectFilterProvider {
  constructor(valueIds?: LINKED_FILTER_PROVIDER_VALUE_KEYS[], options?: SuppliedFilterProviderOptions) {
    const opts = {
      ...options,
      filterTypes: {...options?.filterTypes, multiValue: false, valueless: false},
    }
    const values = valueIds?.map(id => LINKED_FILTER_PROVIDER_VALUES[id]).filter(Boolean) ?? []
    super(FILTER_KEYS.linked, values?.length > 0 ? values : FILTER_VALUES.linked, opts)
  }
}

export class ReasonFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.reason, FILTER_VALUES.reason, {
      ...options,
      filterTypes: {hasValue: false, ...options?.filterTypes},
    })
  }
}

export class ReviewFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.review, FILTER_VALUES.review, options)
  }
}

export class SortFilterProvider extends SelectFilterProvider {
  constructor(valueIds: SORT_FILTER_PROVIDER_VALUE_KEYS[], options?: SuppliedFilterProviderOptions) {
    const sortValues =
      valueIds
        .map(id => SORT_FILTER_PROVIDER_VALUES[id])
        .flat()
        .filter(Boolean) ?? []
    const reactionValues = valueIds?.includes('reactions') ? SORT_REACTIONS_FILTER_PROVIDER_VALUES : []

    const values = [...sortValues, ...reactionValues]
    super(FILTER_KEYS.sort, values?.length > 0 ? values : FILTER_VALUES.sort, options)
  }
}

export type StateFilterProviderType = 'mixed' | 'memex' | 'issues' | 'pr'
export class StateFilterProvider extends SelectFilterProvider {
  stateContext: StateFilterProviderType

  constructor(type: StateFilterProviderType = 'mixed', options?: SuppliedFilterProviderOptions) {
    const values = getFilterValuesByStateType(type)
    const defaultOptions: SuppliedFilterProviderOptions = {filterTypes: {valueless: false}}
    super(FILTER_KEYS.state, values, {
      ...defaultOptions,
      ...options,
      filterTypes: {...defaultOptions.filterTypes, hasValue: false, ...options?.filterTypes},
    })
    this.stateContext = type
  }
}

export class StatusFilterProvider extends SelectFilterProvider {
  constructor(options?: SuppliedFilterProviderOptions) {
    super(FILTER_KEYS.status, FILTER_VALUES.status, options)
  }
}
